import { useRef, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, formatMoney, hoy, parseMoney, type Centavos } from '@abastece/shared';
import { db, getMeta } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { abrirCajaYVer, fondoQueQuedo, sesionAbierta } from '../lib/caja.js';
import { primeroDelDia } from '../lib/dia.js';
import { anterior, atrasHasta, irA } from '../lib/volver.js';
import { Asistente, Respuestas, indicePaso, type Paso } from '../ui/Asistente.js';
import { Campo } from '../ui/Campo.js';
import { enMoneda } from '../ui/ContarBilletes.js';
import { CuentasDeApertura, Dato, type Apertura } from '../ui/CuentasDeCaja.js';
import { Ico } from '../ui/Iconos.js';
import { ListaDeAvisos, ListaDePedidos, useAvisosDeInventario, usePedidosPendientes } from '../ui/LoDelDia.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { SIN_CONTAR, contado, pasoDeConteo, primerConteo, type Contando } from '../ui/PasoDeConteo.js';
import { useT } from '../i18n/index.js';

/**
 * «Empezar el día» y «Empezar mi turno»: abrir la caja, un paso a la vez.
 *
 * What opening a till asks, one question to a screen (the owner, 2026-10-03): whether money was
 * brought from general cash, then the drawer, counted note by note, córdobas and then dollars, and
 * what that count comes to against what the last shift left and what was brought. A drawer with no
 * float is asked too, as «¿Puso dinero para el cambio?» (the owner, 2026-10-14: «there should also
 * be a step for putting in cash for change»), and «No hay córdobas» says an empty one in a tap. A
 * count off by `cajaReconteoDesde` or more is asked again first, without saying by how much or
 * which way; the second one stands, and the owner sees both. The day's first start is «Empezar el
 * día» and goes on to what the day holds: the orders to deliver and the stock that wants seeing to.
 * A start after another shift has closed here today is «Empezar mi turno», and is the drawer alone.
 *
 * The till opens at the last question; what follows is said over a till already open, so nothing
 * before it can be gone back to.
 */
export function EmpezarDia() {
  const t = useT();
  /** An order «Cobrar» sent to the till before it was open: the till loads it once it is. */
  const { pedido } = useSearch({ from: '/caja/empezar' });
  /**
   * The screen that sent here, if it was one that sells: the till, an order perhaps waiting in its
   * address, or «Recargas». The till open, «Vender» goes back to it rather than open another.
   */
  const [vino] = useState(() => anterior()?.pathname);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The till open on this device: null for none, undefined while it is read. */
  const abiertaAqui = useLiveQuery(async () => (await sesionAbierta()) ?? null, [], undefined);
  const dispositivoId = useLiveQuery(() => getMeta('dispositivoId'), [], undefined);
  const cerradas = useLiveQuery(() => db.sesiones.where('estado').equals('cerrada').toArray(), [], undefined);

  // The till sells from the shop, the one place there is (the owner, 2026-10-05).
  const ubicacion = 'tienda';
  /**
   * What the last corte here left, which the count is held against. Undefined while it is read:
   * opening before it is known would take a float left overnight for one brought from general cash.
   */
  const relevo = useLiveQuery(() => fondoQueQuedo(ubicacion), [ubicacion]);
  const conRelevo = relevo != null && relevo.queda > 0;

  const [actual, setActual] = useState('empezar');
  /** Whether money was put in from general cash: unanswered, no, or yes and how much. */
  const [puso, setPuso] = useState<boolean | null>(null);
  const [puesto, setPuesto] = useState('');
  const [cordobas, setCordobas] = useState<Contando>(SIN_CONTAR);
  const [dolares, setDolares] = useState<Contando>(SIN_CONTAR);
  const [abriendo, setAbriendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Where the step on screen was, for when it goes (`indicePaso`). */
  const ultimo = useRef(0);
  /** The till this opened, and what its count found: from here on the steps say, and ask nothing. */
  const [abierta, setAbierta] = useState<{ apertura: Apertura | null } | null>(null);
  /** Whether this is the day's first start: worked out until the till opens, then kept. */
  const [diaFijo, setDiaFijo] = useState<boolean | null>(null);
  const primero =
    diaFijo ?? (cerradas && dispositivoId ? primeroDelDia(cerradas, dispositivoId, ubicacion, hoy()) : true);

  const pedidos = usePedidosPendientes('hoy');
  const avisos = useAvisosDeInventario();

  const montoPuesto = puso ? parseMoney(puesto) : (0 as Centavos);
  const puestoValido = montoPuesto !== null && montoPuesto >= 0 && (!puso || montoPuesto > 0);
  const totalC = contado(cordobas, 'NIO');
  const totalD = contado(dolares, 'USD');
  /** What the córdobas count is held against: what the last corte left, if anything, and what was put in. */
  const esperadoC = montoPuesto !== null ? (((conRelevo ? relevo.queda : 0) + montoPuesto) as Centavos) : null;

  const titulo = t(primero ? 'turno.empezarDia' : 'turno.empezarTurno');

  if (abiertaAqui === undefined || relevo === undefined) {
    return <Pagina titulo={titulo}>{null}</Pagina>;
  }
  // A till already open here from before: it is ended before another one starts.
  if (abiertaAqui && !abierta && !abriendo) {
    return (
      <Pagina titulo={titulo} volverA="/caja">
        <Vacio mensaje={t('turno.yaAbierta')} icono="caja">
          <Link to="/caja" className="btn-primario px-5">
            <Ico icono="caja" />
            {t('turno.verCaja')}
          </Link>
        </Vacio>
      </Pagina>
    );
  }

  async function abrir() {
    if (totalC === null || totalD === null || montoPuesto === null || relevo === undefined) return;
    setAbriendo(true);
    setError(null);
    const primeraCuenta = primerConteo(cordobas, totalC);
    try {
      const { rechazo } = await abrirCajaYVer(totalC, ubicacion, totalD, {
        relevo,
        puesto: montoPuesto,
        ...(primeraCuenta !== null ? { primerConteo: primeraCuenta } : {}),
      });
      // Refused (another till opened here meanwhile): said here, and nothing claims it is open.
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setDiaFijo(primero);
      setAbierta({
        apertura: { quedo: conRelevo ? relevo.queda : (0 as Centavos), puesto: montoPuesto, contado: totalC, primerConteo: primeraCuenta },
      });
      // On to the first of the steps that follow, whichever they turn out to be.
      setActual('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setAbriendo(false);
    }
  }


  // --- Before the till opens: each step can be gone back to. ---
  const antes: Paso[] = [
    {
      id: 'empezar',
      titulo,
      guia: 'dia-empezar',
      explicacion: <p>{t(primero ? 'turno.empezarDiaExplicacion' : 'turno.empezarTurnoExplicacion')}</p>,
      seguir: { etiqueta: t('turno.comenzar'), alTocar: () => siguiente() },
    },
    // Money brought from general cash: on top of a float left, or, with none, the change to start with.
    {
      id: 'puesto',
      titulo: t(conRelevo ? 'turno.pusoTitulo' : 'turno.pusoCambioTitulo'),
      explicacion: <p>{t(conRelevo ? 'turno.pusoExplicacion' : 'turno.pusoCambioExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Respuestas
            opciones={[
              {
                etiqueta: t('turno.pusoNo'),
                elegida: puso === false,
                icono: 'listo',
                alTocar: () => {
                  setPuso(false);
                  setPuesto('');
                  siguiente();
                },
              },
              { etiqueta: t('turno.pusoSi'), icono: 'ingresar', alTocar: () => setPuso(true), elegida: puso === true },
            ]}
          />
          {puso && (
            <Campo
              etiqueta={t('turno.cuantoPuso')}
              inputMode="decimal"
              className="tabular text-lg"
              value={puesto}
              onChange={(e) => setPuesto(e.target.value)}
              placeholder="0.00"
              autoFocus
              error={puesto.trim() && !puestoValido ? t('caja.montoInvalido') : null}
              ayuda={t('turno.cuantoPusoAyuda')}
            />
          )}
        </div>
      ),
      ...(puso
        ? { seguir: { etiqueta: t('asistente.siguiente'), deshabilitado: !puestoValido, alTocar: () => siguiente() } }
        : {}),
    } satisfies Paso,
    pasoDeConteo({
      id: 'cordobas',
      moneda: 'NIO',
      contando: cordobas,
      poner: setCordobas,
      esperado: esperadoC,
      umbral: config.cajaReconteoDesde,
      titulo: t('turno.contarCordobas'),
      explicacion:
        conRelevo && !config.cajaConteoCiego && esperadoC !== null
          ? t('turno.contarCordobasVisible', { monto: formatMoney(esperadoC) })
          : t('turno.contarCordobasExplicacion'),
      sinCordobas: true,
      // What was put in at the step before is in the drawer, and counted with the rest (the owner,
      // 2026-10-14: «it should say in step 3 … that you are also counting that money»).
      ...(puso && montoPuesto ? { nota: t('turno.contarLoPuesto', { monto: formatMoney(montoPuesto) }) } : {}),
      siguiente: () => siguiente(),
      t,
    }),
    pasoDeConteo({
      id: 'dolares',
      moneda: 'USD',
      contando: dolares,
      poner: setDolares,
      // Every dollar left with the last corte: any in the drawer now were brought, and are counted in.
      esperado: null,
      umbral: config.cajaReconteoDesde,
      titulo: t('turno.contarDolares'),
      explicacion: t('turno.contarDolaresAbrir'),
      siguiente: () => siguiente(),
      t,
    }),
    {
      id: 'confirmar',
      titulo: t('turno.revise'),
      explicacion: <p>{t('turno.reviseAbrir')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            <Dato etiqueta={t('caja.fondoCordobas')} valor={formatMoney(totalC ?? (0 as Centavos))} />
            <Dato etiqueta={t('caja.fondoDolares')} valor={enMoneda(totalD ?? (0 as Centavos), 'USD')} />
            {(montoPuesto ?? 0) > 0 && <Dato etiqueta={t('caja.puestoDeGeneral')} valor={formatMoney(montoPuesto!)} />}
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: {
        etiqueta: titulo,
        icono: 'caja',
        deshabilitado: abriendo || totalC === null || totalD === null || montoPuesto === null,
        alTocar: () => void abrir(),
      },
    },
  ];

  // --- Once the till is open: what the count found, and what the day holds. ---
  const despues: Paso[] = [
    // What the count found against what the last shift left and what was put in; with no float,
    // against what was put in alone (2026-10-14: every opening is asked it). Counted before the
    // till opens too, so the step count does not grow at the last question.
    {
      id: 'apertura',
      titulo: t((abierta?.apertura ? abierta.apertura.quedo > 0 : conRelevo) ? 'turno.loQueDejo' : 'turno.loQueEncontro'),
      guia: 'caja-abrir',
      sinAtras: true,
      contenido: abierta?.apertura ? <CuentasDeApertura apertura={abierta.apertura} /> : null,
      seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
    } satisfies Paso,
    ...(primero && (pedidos?.length ?? 0) > 0
      ? [
          {
            id: 'pedidos',
            titulo: t('turno.pedidosHoy'),
            sinAtras: true,
            explicacion: <p>{t('turno.pedidosHoyExplicacion')}</p>,
            contenido: <ListaDePedidos pedidos={pedidos!} />,
            seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
          } satisfies Paso,
        ]
      : []),
    ...(primero && (avisos?.length ?? 0) > 0
      ? [
          {
            id: 'avisos',
            titulo: t('turno.avisosTitulo'),
            sinAtras: true,
            explicacion: <p>{t('turno.avisosExplicacion')}</p>,
            contenido: <ListaDeAvisos avisos={avisos!} />,
            seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => siguiente() },
          } satisfies Paso,
        ]
      : []),
    {
      id: 'listo',
      titulo: t('turno.abiertaTitulo'),
      guia: 'dia-abierta',
      sinAtras: true,
      explicacion: <p>{t('turno.abiertaExplicacion')}</p>,
      contenido: (
        <Link to="/" replace onClick={atrasHasta('/')} className="btn-secundario">
          <Ico icono="inicio" />
          {t('turno.irAlInicio')}
        </Link>
      ),
      seguir:
        vino === '/recargas'
          ? { etiqueta: t('recargas.volver'), icono: 'recargas', alTocar: () => void irA({ to: '/recargas' }) }
          : {
              etiqueta: t('inicio.vender'),
              icono: 'vender',
              alTocar: () => void irA({ to: '/pos', ...(pedido ? { search: { pedido } } : {}) }),
            },
    },
  ];

  const pasos = abierta ? despues : antes;
  const ids = pasos.map((p) => p.id);
  const i = indicePaso(ids, actual, ultimo.current);
  ultimo.current = i;
  const paso = pasos[i]!;
  function siguiente() {
    setActual(ids[i + 1] ?? ids[i]!);
  }
  // Numbered across every step, before the till opens and after, so the number only grows.
  const todos = [...antes, ...despues].map((p) => p.id);

  return (
    <Asistente
      titulo={titulo}
      subtitulo={t('turno.subtitulo')}
      paso={paso}
      numero={todos.indexOf(paso.id) + 1}
      total={todos.length}
      volverA="/caja"
      {...(i > 0 ? { onAtras: () => setActual(ids[i - 1]!) } : {})}
    />
  );
}
